import {
  Gavel,
  FileText,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type {
  LineItem,
  CreateEventFormData,
} from './create-event-sections';

const CATEGORIES = [
  'Fresh Produce',
  'Frozen Seafood',
  'Dry Goods',
  'Dairy',
  'Beverages',
  'Meat & Poultry',
];

const UNITS = ['kg', 'lb', 'case', 'each', 'liter', 'gallon'];

interface LineItemsSectionProps {
  readonly formData: CreateEventFormData;
  readonly setFormData: (data: CreateEventFormData) => void;
}

export function LineItemsSection({
  formData, setFormData 
}: LineItemsSectionProps) {
  const handleAddLine = () => {
    const newLine: LineItem = {
      id: crypto.randomUUID(),
      itemName: '',
      sku: '',
      category: '',
      quantity: '',
      unit: 'kg',
      specifications: '',
    };
    setFormData({
      ...formData,
      lines: [...formData.lines, newLine] 
    });
  };

  const handleRemoveLine = (id: string) => {
    setFormData({
      ...formData,
      lines: formData.lines.filter((l) => l.id !== id) 
    });
  };

  const handleLineChange = (id: string, field: keyof LineItem, value: string) => {
    setFormData({
      ...formData,
      lines: formData.lines.map((l) => (l.id === id ? {
        ...l,
        [field]: value 
      } : l)),
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Line Items</CardTitle>
            <CardDescription>Items to be sourced in this event</CardDescription>
          </div>
          <Button onClick={handleAddLine} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {formData.lines.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No line items added yet</p>
            <Button onClick={handleAddLine} variant="outline" className="mt-2">
              <Plus className="h-4 w-4 mr-2" />
              Add First Item
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {formData.lines.map((line, index) => (
              <div key={line.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Item {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveLine(line.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Item Name</label>
                    <input
                      type="text"
                      value={line.itemName}
                      onChange={(e) => handleLineChange(line.id, 'itemName', e.target.value)}
                      placeholder="Atlantic Salmon"
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">SKU</label>
                    <input
                      type="text"
                      value={line.sku}
                      onChange={(e) => handleLineChange(line.id, 'sku', e.target.value)}
                      placeholder="SEA-SAL-001"
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Category</label>
                    <select
                      value={line.category}
                      onChange={(e) => handleLineChange(line.id, 'category', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    >
                      <option value="">Select...</option>
                      {CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Quantity</label>
                    <input
                      type="number"
                      min="0"
                      value={line.quantity}
                      onChange={(e) => handleLineChange(line.id, 'quantity', e.target.value)}
                      placeholder="500"
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Unit</label>
                    <select
                      value={line.unit}
                      onChange={(e) => handleLineChange(line.id, 'unit', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    >
                      {UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Specifications</label>
                    <input
                      type="text"
                      value={line.specifications}
                      onChange={(e) => handleLineChange(line.id, 'specifications', e.target.value)}
                      placeholder="Fresh, skin-on"
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface SummarySectionProps {
  readonly formData: CreateEventFormData;
  readonly setFormData: (data: CreateEventFormData) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

export function SummarySection({
  formData, setFormData, onSubmit, onCancel 
}: SummarySectionProps) {
  const isReverseAuction = formData.eventType === 'reverse_auction';
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">Event Type</p>
            <p className="font-medium flex items-center gap-2">
              {isReverseAuction ? (
                <>
                  <Gavel className="h-4 w-4 text-orange-600" />
                  Reverse Auction
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 text-indigo-600" />
                  Sealed Proposal
                </>
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Line Items</p>
            <p className="font-medium">{formData.lines.length} items</p>
          </div>
          {formData.deliveryZone && (
            <div>
              <p className="text-sm text-muted-foreground">Delivery Zone</p>
              <p className="font-medium">{formData.deliveryZone}</p>
            </div>
          )}
          {formData.bidDeadline && (
            <div>
              <p className="text-sm text-muted-foreground">Bid Deadline</p>
              <p className="font-medium">
                {new Date(formData.bidDeadline).toLocaleString()}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Estimated Value ($)</label>
            <input
              type="number"
              min="0"
              value={formData.estimatedValue}
              onChange={(e) => setFormData({
                ...formData,
                estimatedValue: e.target.value 
              })}
              placeholder="50000"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <Button onClick={onSubmit} className="w-full" size="lg">
            Create Event
          </Button>
          <Button
            variant="outline"
            className="w-full mt-2"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
